import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../popups.css";
import {
  SETTINGS_SUBSCRIPTION_PATH,
  TREE_SIDE_API_FATHER,
  TREE_SIDE_API_MOTHER,
} from "../Utils/subscriptionAccess";

/** Same two root-line choices as `RootHeadModal` “Set Start/Top of the family tree”. */
const OPTIONS = [
  {
    value: TREE_SIDE_API_MOTHER,
    label: "Grandfather & Grandmother (Mother's Side)",
    rootFamilyLineSide: "mother",
  },
  {
    value: TREE_SIDE_API_FATHER,
    label: "Grandfather & Grandmother (Father's Side)",
    rootFamilyLineSide: "father",
  },
];

/**
 * Custom head-of-line listbox matching `RootHeadModal` (button + chevron + list, plan-locked rows + hint).
 * `onChange` receives API `tree_side` strings: "Mother Side" | "Father Side".
 */
export default function FamilyLineSideHeadDropdown({
  value,
  onChange,
  headLineRestriction,
  loading = false,
  disabled = false,
  /** When true (e.g. header is on Mother Side and that tree exists), selection is fixed and the control is not editable. */
  selectionLocked = false,
  /** `"both"` show both lines; `"mother"` | `"father"` show only that line (other side hidden). */
  visibleSides = "both",
  label = "Select Names for Top of the Line",
  helperText = "",
  id = "familyLineSideHead",
  listboxId = "family-line-side-head-listbox",
}) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const goSubscriptionSettings = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setMenuOpen(false);
    navigate(SETTINGS_SUBSCRIPTION_PATH);
  };

  const optionsToShow = useMemo(() => {
    if (visibleSides === "both") return OPTIONS;
    return OPTIONS.filter((o) => o.rootFamilyLineSide === visibleSides);
  }, [visibleSides]);

  const selectedOpt = optionsToShow.find((o) => o.value === value);
  const displayLabel = selectedOpt?.label || "";

  const chevronSvg = (
    <svg
      style={{
        position: "absolute",
        right: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        width: "30px",
        height: "30px",
        fill: "#666",
      }}
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
    </svg>
  );

  const inactive = loading || disabled || selectionLocked;

  return (
    <div className="form-group roothead-group">
      {label ? <label htmlFor={id}>{label}</label> : null}
      {helperText ? (
        <p className="popup-desc" style={{ marginTop: 4, marginBottom: 8 }}>
          {helperText}
        </p>
      ) : null}
      <div
        className="roothead-head-dropdown-shell"
        style={{ position: "relative" }}
        ref={menuRef}
      >
        <button
          type="button"
          id={id}
          className={`dropdown roothead-dropdown form-control roothead-head-dropdown-toggle ${!value ? "placeholder-bold" : ""}`}
          disabled={inactive}
          aria-expanded={menuOpen}
          aria-haspopup="listbox"
          onClick={() => !inactive && setMenuOpen((open) => !open)}
          style={{
            width: "100%",
            textAlign: "left",
            cursor: inactive ? "not-allowed" : "pointer",
            appearance: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
            paddingRight: "36px",
          }}
        >
          {loading
            ? "Loading..."
            : displayLabel || "Choose Head of Family"}
        </button>
        {chevronSvg}
        {menuOpen && !inactive && (
          <ul
            className="roothead-head-dropdown-list roothead-head-dropdown-list--restricted"
            role="listbox"
            id={listboxId}
          >
            {optionsToShow.map((option) => {
              const val = option.value;
              const itemLabel = option.label;
              const locked =
                (headLineRestriction === "mother_only" &&
                  option.rootFamilyLineSide === "father") ||
                (headLineRestriction === "father_only" &&
                  option.rootFamilyLineSide === "mother");
              const isSelected = value === val;
              if (locked) {
                return (
                  <li
                    key={val}
                    className="roothead-head-dropdown-item--disabled-wrap"
                  >
                    <div
                      className="roothead-head-dropdown-item is-disabled"
                      aria-disabled="true"
                    >
                      {itemLabel}
                    </div>
                    <div
                      className="roothead-head-plan-hint-popover"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <p className="roothead-head-plan-hint-text">
                        <>
                          Your current plan allows you to manage a single family line.{" "}
                          Upgrade your subscription to add another family line.
                        </>
                      </p>
                      <button
                        type="button"
                        className="btn-primary roothead-hint-upgrade-btn"
                        onClick={goSubscriptionSettings}
                      >
                        Subscription settings
                      </button>
                    </div>
                  </li>
                );
              }
              return (
                <li
                  key={val}
                  role="option"
                  aria-selected={isSelected}
                  className={`roothead-head-dropdown-item ${isSelected ? "is-selected" : ""}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange?.(val);
                    setMenuOpen(false);
                  }}
                >
                  {itemLabel}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
